import { finalize, catchError, map, switchMap } from 'rxjs/operators';
import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { IAppConfig, APP_CONFIG } from '../../../app.config';
import { Observable, of } from 'rxjs';
import { LocalDate } from '../../../core/model/local-date';
import { DataService } from '../../../core/service/base/data.service';
import { InvoicePeriodItem } from '../../model/invoice-period';
import { Company } from '../../../rent/model/company';
import { ContactBalanceTransaction } from '../../../rent/model/contact-balance-transaction';
import { Money } from '../../../core/model/money';
import { ContactCompanyBalance } from '../../model/contact-company-balance';
import { ContactBalanceRepayment } from '../../model/contact-balance-repayment';
import { ContactBalanceMove, ContactCompanyBalanceMove } from '../../model/contact-balance-move';

@Injectable()
export class ContactBalanceTransactionService extends DataService<ContactBalanceTransaction> {
  private readonly _defaultFilterFields: string[] = ['company', 'fromDate', 'toDate'];

  constructor(
    private _client: HttpClient,
    private _messageService: MessageService,
    private _progressBar: ProgressBarService,
    @Inject(APP_CONFIG) config: IAppConfig
  ) {
    super(_client, config, _messageService, ContactBalanceTransaction, _progressBar, 'contact-balance-transaction');
  }

  getFilterDefinition(): Observable<{
    company: string;
    companies: Company[];
    fromDate: string;
    toDate: string;
    contactName: string;
    amount: Money;
    balance: Money;
    hideZeroBalance: boolean;
    dateLimits: InvoicePeriodItem[];
    selectedFields: string[];
    defaultFields: string[];
  }> {
    return this.client.get<any>(`${this.endpoint}/filter`).pipe(
      map(res => {
        return {
          company: res.company ? res.company : '',
          companies: Array.isArray(res.companies) ? res.companies.map(x => Object.assign(new Company(), x)) : [],
          contactName: res.contactName ?? '',
          amount: res.amount,
          balance: res.balance,
          hideZeroBalance: res.hideZeroBalance ?? false,
          fromDate: res.fromDate ? new LocalDate(res.fromDate.year, res.fromDate.month, 1).toISOString() : res.fromDate,
          toDate: res.toDate
            ? new LocalDate(res.toDate.year, res.toDate.month, LocalDate.daysInMonth(res.toDate.year, res.toDate.month)).toISOString()
            : res.toDate,
          dateLimits: Array.isArray(res.dateLimits) ? res.dateLimits.map(x => Object.assign(new InvoicePeriodItem(), x)) : [],
          selectedFields: Array.isArray(res.selectedFields) ? res.selectedFields : this._defaultFilterFields,
          defaultFields: this._defaultFilterFields,
        };
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  export(ids: string[]): Observable<File> {
    return super.exportDataItems(ids, 'Tillgodo.xlsx');
  }

  mapFilterValue(key: string, value: any): any {
    if ((key === 'fromDate' || key === 'toDate') && value) return LocalDate.fromDate(value);
    return super.mapFilterValue(key, value);
  }

  getCompanyBalances(contactId: string): Observable<ContactCompanyBalance[]> {
    return this.client.get<ContactCompanyBalance[]>(`${this.endpoint}/company-balances/${contactId}`);
  }

  repay(repayment: ContactBalanceRepayment): Observable<ContactBalanceTransaction[]> {
    return this.client.post<ContactBalanceTransaction[]>(`${this.endpoint}/repay`, repayment).pipe(
      map(res => this.toModels(res)),
      catchError(error => {
        this._messageService.showError(error);
        throw error;
      })
    );
  }

  moveBalance(move: ContactBalanceMove): Observable<ContactBalanceTransaction[]> {
    return this.client.patch<ContactBalanceMove>(`${this.endpoint}/move`, move).pipe(
      map(res => this.toModels(res)),
      catchError(error => {
        this._messageService.showError(error);
        throw error;
      })
    );
  }
}
